import { useMemo, useEffect, useRef, useState } from 'react';
import { buildSvg, buildSvgAsync, ExtendedField } from '../generators/svgGenerator';
import { ProductTemplate, TemplateShape } from '../types/dbTypes';
import { Font } from '../types/dbTypes';
import { waitForFontsLoaded } from '../utils/fontLoader';

interface SvgPreviewProps {
    width: number;
    height: number;
    fields: ExtendedField[];
    values: Record<string, string>;
    template?: ProductTemplate | null;
    shapes?: TemplateShape[];
    className?: string;
    showBackground?: boolean; // Whether to show template background SVG
    fonts?: Font[];  // Optional fonts for glyph-based rendering
}

/**
 * SvgPreview - Pure DOM SVG rendering component
 *
 * This component renders the exact SVG that will be sent to the laser machine.
 * Unlike Fabric.js canvas, this uses the browser's native SVG rendering engine,
 * ensuring perfect font rendering and exact match with final output.
 *
 * Benefits:
 * - 100% match with final laser output
 * - Reliable custom font rendering via CSS @font-face
 * - No canvas positioning hacks needed
 * - Faster rendering than Fabric.js
 * - Easy to inspect in browser DevTools
 */
export default function SvgPreview({
    width,
    height,
    fields,
    values,
    template,
    shapes = [],
    className = '',
    showBackground = true,
    fonts = []
}: SvgPreviewProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const [fontsReady, setFontsReady] = useState(false);
    const [generatedSvg, setGeneratedSvg] = useState('');

    // Wait for fonts to load before rendering
    useEffect(() => {
        waitForFontsLoaded().then(() => {
            setFontsReady(true);
        });
    }, []);

    // Generate SVG using glyph-based rendering when fonts are available
    useEffect(() => {
        if (!fontsReady) {
            return;
        }

        const svgWidth = template?.width || width;
        const svgHeight = template?.height || height;

        const generateSvg = async () => {
            try {
                // Use glyph-based rendering if fonts are provided
                if (fonts && fonts.length > 0) {
                    const svg = await buildSvgAsync(shapes, fields, values, svgWidth, svgHeight, fonts);
                    setGeneratedSvg(svg);
                } else {
                    // Fallback to sync version (uses text elements)
                    const svg = buildSvg(shapes, fields, values, svgWidth, svgHeight);
                    setGeneratedSvg(svg);
                }
            } catch (error) {
                console.error('[SvgPreview] Error generating SVG:', error);
                setGeneratedSvg(`<svg xmlns="http://www.w3.org/2000/svg" width="${svgWidth}" height="${svgHeight}" viewBox="0 0 ${svgWidth} ${svgHeight}"><text x="50%" y="50%" text-anchor="middle" fill="red">Error generating preview</text></svg>`);
            }
        };

        generateSvg();
    }, [shapes, fields, values, template, width, height, fontsReady, fonts]);

    // Extract background SVG if template has one
    // Don't show background if we have graphic shapes (they'll be rendered via buildSvg)
    const backgroundSvg = useMemo(() => {
        if (!showBackground || !template?.svgContent) {
            return null;
        }

        // If we have graphic shapes, don't render background (avoids double-rendering)
        const hasGraphicShapes = shapes.some(s => s.type === 'graphic');
        if (hasGraphicShapes) {
            return null;
        }

        // Check if template has actual SVG content (not just empty template)
        if (template.svgContent.includes('<svg') && template.svgContent.length > 100) {
            return template.svgContent;
        }

        return null;
    }, [template, showBackground, shapes]);

    if (!fontsReady) {
        return (
            <div className={`flex items-center justify-center bg-white ${className}`}>
                <p className="text-slate-400 text-sm">Loading fonts...</p>
            </div>
        );
    }

    const svgWidth = template?.width || width;
    const svgHeight = template?.height || height;

    // Check if we have any content to display
    const hasContent = shapes.length > 0 || backgroundSvg;

    return (
        <div
            ref={containerRef}
            className={`bg-white ${className}`}
            style={{
                width: `${svgWidth}px`,
                height: `${svgHeight}px`,
                position: 'relative'
            }}
        >
            {!hasContent && (
                <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-sm">
                    <div className="text-center">
                        <p>No template configured</p>
                        <p className="text-xs mt-1">Configure shapes in product settings</p>
                    </div>
                </div>
            )}

            {/* Background SVG (template) - rendered behind */}
            {backgroundSvg && (
                <div
                    className="absolute inset-0 pointer-events-none"
                    style={{ opacity: 0.3 }}
                    dangerouslySetInnerHTML={{
                        __html: backgroundSvg
                            .replace(/width="[^"]*mm?"/, `width="100%"`)
                            .replace(/height="[^"]*mm?"/, `height="100%"`)
                            .replace(/<svg/, `<svg preserveAspectRatio="xMidYMid meet"`)
                    }}
                />
            )}

            {/* Generated SVG (text and shapes) - rendered on top */}
            {generatedSvg && (
                <div
                    className="absolute inset-0"
                    dangerouslySetInnerHTML={{
                        __html: generatedSvg
                            .replace(/width="[^"]*mm?"/, `width="100%"`)
                            .replace(/height="[^"]*mm?"/, `height="100%"`)
                            .replace(/<svg/, `<svg preserveAspectRatio="xMidYMid meet"`)
                    }}
                />
            )}
        </div>
    );
}
